import React, {
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
    useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useSearchParams } from "react-router-dom";
import { useFormikContext, Field, FormikValues } from "formik";

import { bemClassName } from "../../utils/bemClassName";
import { joinClassNames } from "../../utils/joinClassNames";
import { useSearchFormContext } from "../../context/SearchFormContext";
import { useSearchResults } from "../../context/DataContext";
import { fetchSuggest } from "../../utils/fetchSuggest";

import styles from "./QueryField.module.css";

const SUGGEST_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 1;

type DropdownPosition = { top: number; left: number; width: number };

export const QueryField = () => {
    const { values, setFieldValue } = useFormikContext() as FormikValues;
    const { query } = useSearchFormContext();
    const { config } = useSearchResults();
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    const { id, label, placeholder, type } = query;

    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [dropdownPosition, setDropdownPosition] =
        useState<DropdownPosition | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const suggestPortalRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const userHasTypedRef = useRef(false);

    const partialQuery = (values.query ?? "").trim();

    const closeSuggestions = useCallback(() => {
        setIsOpen(false);
        setActiveIndex(0);
    }, []);

    const handleQueryChange = useCallback(() => {
        userHasTypedRef.current = true;
    }, []);

    useEffect(() => {
        if (
            !userHasTypedRef.current ||
            partialQuery.length < MIN_QUERY_LENGTH ||
            !config?.endpoint ||
            !config?.collection
        ) {
            setSuggestions([]);
            setIsOpen(false);
            return;
        }

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            debounceRef.current = null;
            setLoading(true);
            fetchSuggest(config, partialQuery)
                .then((list) => {
                    setSuggestions(list);
                    setIsOpen(list.length > 0);
                    setActiveIndex(0);
                })
                .catch(() => {
                    setSuggestions([]);
                    setIsOpen(false);
                })
                .finally(() => {
                    setLoading(false);
                });
        }, SUGGEST_DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [partialQuery, config.endpoint, config.collection]);

    const updateDropdownPosition = useCallback(() => {
        if (!wrapperRef.current || !isOpen || suggestions.length === 0) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        setDropdownPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width,
        });
    }, [isOpen, suggestions.length]);

    useLayoutEffect(() => {
        if (!isOpen || suggestions.length === 0) {
            setDropdownPosition(null);
            return;
        }
        updateDropdownPosition();
        window.addEventListener("scroll", updateDropdownPosition, true);
        window.addEventListener("resize", updateDropdownPosition);
        return () => {
            window.removeEventListener("scroll", updateDropdownPosition, true);
            window.removeEventListener("resize", updateDropdownPosition);
        };
    }, [isOpen, suggestions.length, updateDropdownPosition]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                wrapperRef.current?.contains(target) ||
                suggestPortalRef.current?.contains(target)
            ) {
                return;
            }
            closeSuggestions();
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [closeSuggestions]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            if (isOpen && suggestions.length > 0) {
                const selected = suggestions[activeIndex]?.trim();
                if (selected) setFieldValue("query", selected);
                closeSuggestions();
            }
            return;
        }
        if (!isOpen || suggestions.length === 0) return;
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setActiveIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
                break;
            case "ArrowUp":
                e.preventDefault();
                setActiveIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
                break;
            case "Escape":
                e.preventDefault();
                closeSuggestions();
                break;
            default:
                break;
        }
    };

    type OptionalFieldProps = {
        [key: string]: boolean | string | undefined;
        disabled?: boolean;
        id?: string;
        placeholder?: string;
    };
    const optionalPropDefs: OptionalFieldProps = {
        id,
        placeholder,
    };
    const optionalProps: OptionalFieldProps = {};
    Object.keys(optionalPropDefs).forEach((prop) => {
        const propValue = optionalPropDefs[prop];
        if (propValue) {
            optionalProps[prop] = propValue;
        }
    });

    return (
        <React.Fragment>
            <div className={styles.searchRow}>
                <div
                    ref={wrapperRef}
                    className={joinClassNames([
                        "twitter-typeahead",
                        styles.queryFieldWrapper,
                    ])}
                >
                    <label className="sr-only" htmlFor={id}>
                        Search by keywords
                    </label>
                    <div className={styles.inputWithSuggest}>
                        <Field
                            type={type}
                            name="query"
                            aria-label={label}
                            aria-autocomplete="list"
                            aria-expanded={isOpen}
                            aria-controls={
                                isOpen ? "search-suggestions-list" : undefined
                            }
                            aria-activedescendant={
                                isOpen && suggestions[activeIndex]
                                    ? `search-suggestion-${activeIndex}`
                                    : undefined
                            }
                            value={values.query}
                            onKeyDown={handleKeyDown}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                handleQueryChange();
                                setFieldValue("query", e.target.value);
                            }}
                            {...optionalProps}
                            className={joinClassNames([
                                "form-control",
                                "input-lg",
                                "query",
                                bemClassName("planning-search-field", "query"),
                            ])}
                        />
                        {loading && partialQuery.length >= MIN_QUERY_LENGTH && (
                            <span
                                className={styles.suggestLoading}
                                aria-live="polite"
                            >
                                Searching…
                            </span>
                        )}
                    </div>
                </div>
                <button
                    type="submit"
                    className={joinClassNames([
                        bemClassName("find-a-panel", "search-btn"),
                        bemClassName("planning-search-form", "submit"),
                        styles.submitButton,
                    ])}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        id="icon_search"
                        className={joinClassNames([
                            bemClassName("planning-search-form", "submit-icon"),
                            "icon",
                            "icon_search",
                        ])}
                        width="24"
                        height="24"
                        role="img"
                        aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10.99 9.243c1.597-2.45 1.224-5.82-1.133-7.833C7.59-.526 4.137-.46 1.94 1.557a6 6 0 00-.19 8.653c2.043 2.045 5.205 2.293 7.527.757.031-.02.102-.083.198-.173l4.343 4.195a.941.941 0 101.308-1.354l-4.324-4.176c.098-.106.166-.184.188-.216zm-8.023-.25a4.274 4.274 0 010-6.035 4.266 4.266 0 016.03 0 4.274 4.274 0 010 6.034 4.266 4.266 0 01-6.03 0z"
                            fill="currentColor"
                        />
                    </svg>
                    <span className="sr-only">Search</span>
                </button>
            </div>
            {isOpen &&
                suggestions.length > 0 &&
                dropdownPosition &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        ref={suggestPortalRef}
                        className={styles.suggestPortalWrapper}
                        style={{
                            position: "fixed",
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            width: dropdownPosition.width,
                            zIndex: 10000,
                        }}
                    >
                        <ul
                            id="search-suggestions-list"
                            className={styles.suggestionsList}
                            role="listbox"
                        >
                            {suggestions.map((s, i) => {
                                const queryValue = s.trim();
                                const href =
                                    typeof window !== "undefined"
                                        ? (() => {
                                              const url = new URL(
                                                  window.location.href
                                              );
                                              url.searchParams.set(
                                                  "query",
                                                  queryValue
                                              );
                                              return url.toString();
                                          })()
                                        : (() => {
                                              const p = new URLSearchParams(
                                                  searchParams
                                              );
                                              p.set("query", queryValue);
                                              return `${pathname}?${p.toString()}`;
                                          })();
                                return (
                                    <li
                                        key={`${s}-${i}`}
                                        id={`search-suggestion-${i}`}
                                        role="option"
                                        aria-selected={i === activeIndex}
                                        className={joinClassNames([
                                            styles.suggestionItem,
                                            i === activeIndex
                                                ? styles.suggestionItemActive
                                                : "",
                                        ])}
                                    >
                                        <a
                                            href={href}
                                            target="_self"
                                            className={styles.suggestionLink}
                                        >
                                            {s}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>,
                    document.body
                )}
        </React.Fragment>
    );
};

export default QueryField;
